import { TrialRaw, ModuleResult, ModuleId, SessionResult, PlatformKind } from './types';
import { mean, best, range, sd, round1, clamp, lerp } from './stats';

export interface Anchor {
  ms: number;
  score: number;
}

/** Ascending ms, descending score */
export const SRT_ANCHORS: Anchor[] = [
  { ms: 140, score: 100 },
  { ms: 160, score: 95 },
  { ms: 180, score: 85 },
  { ms: 200, score: 75 },
  { ms: 230, score: 60 },
  { ms: 270, score: 45 },
  { ms: 320, score: 30 },
  { ms: 400, score: 0 },
];

export const GNG_ANCHORS: Anchor[] = [
  { ms: 180, score: 100 },
  { ms: 200, score: 95 },
  { ms: 220, score: 85 },
  { ms: 240, score: 75 },
  { ms: 270, score: 60 },
  { ms: 310, score: 45 },
  { ms: 360, score: 30 },
  { ms: 450, score: 0 },
];

export const CRT_ANCHORS: Anchor[] = [
  { ms: 200, score: 100 },
  { ms: 220, score: 95 },
  { ms: 240, score: 85 },
  { ms: 260, score: 75 },
  { ms: 290, score: 60 },
  { ms: 330, score: 45 },
  { ms: 380, score: 30 },
  { ms: 480, score: 0 },
];

export const AIM_ANCHORS: Anchor[] = [
  { ms: 180, score: 100 },
  { ms: 220, score: 95 },
  { ms: 250, score: 85 },
  { ms: 300, score: 75 },
  { ms: 340, score: 60 },
  { ms: 380, score: 50 },
  { ms: 450, score: 30 },
  { ms: 550, score: 0 },
];

export function scoreFromMs(ms: number, anchors: Anchor[]): number {
  if (ms <= anchors[0].ms) return anchors[0].score;
  const last = anchors[anchors.length - 1];
  if (ms >= last.ms) return last.score;
  for (let i = 0; i < anchors.length - 1; i++) {
    const a = anchors[i];
    const b = anchors[i + 1];
    if (ms >= a.ms && ms <= b.ms) {
      const t = (ms - a.ms) / (b.ms - a.ms);
      return lerp(a.score, b.score, t);
    }
  }
  return 0;
}

export function srtLabel(meanMs: number): string {
  if (meanMs <= 160) return '职业级';
  if (meanMs <= 180) return '精英';
  if (meanMs <= 200) return '优秀';
  if (meanMs <= 230) return '良好';
  if (meanMs <= 270) return '平均';
  if (meanMs <= 320) return '偏慢';
  return '入门';
}

export function aimLabel(avg: number): string {
  if (avg <= 220) return '职业瞄准';
  if (avg <= 250) return '精英';
  if (avg <= 300) return '优秀';
  if (avg <= 380) return '良好–平均';
  if (avg <= 450) return '偏慢';
  return '入门';
}

export function rankFromScore(score: number): string {
  if (score >= 90) return '电竞职业潜质';
  if (score >= 80) return '竞技精英';
  if (score >= 70) return '优秀玩家';
  if (score >= 55) return '进阶';
  if (score >= 40) return '平均';
  return '入门';
}

function formalTrials(trials: TrialRaw[]): TrialRaw[] {
  return trials.filter((t) => !t.practice);
}

function rtSamples(trials: TrialRaw[]): number[] {
  return formalTrials(trials)
    .filter((t) => !t.foul && t.rtMs != null && Number.isFinite(t.rtMs))
    .map((t) => t.rtMs as number);
}

export function scoreSrt(trials: TrialRaw[]): ModuleResult {
  const flags: string[] = [];
  const samples = rtSamples(trials);
  const fouls = formalTrials(trials).filter((t) => t.foul).length;
  const valid = samples.length >= 5 && fouls <= 3;
  if (!valid) {
    return {
      module: 'srt',
      trials,
      meanMs: samples.length ? round1(mean(samples)) : null,
      bestMs: samples.length ? round1(best(samples)) : null,
      rangeMs: samples.length >= 2 ? round1(range(samples)) : null,
      sdMs: samples.length ? round1(sd(samples)) : null,
      score: 0,
      valid: false,
      label: '无效',
      flags: ['invalid'],
    };
  }
  const m = mean(samples);
  const r = range(samples);
  const s = sd(samples);
  let base = scoreFromMs(m, SRT_ANCHORS);
  if (r <= 30) base += 3;
  else if (r > 50) base -= 8;
  const score = round1(clamp(base, 0, 100));
  if (m < 120 && s < 5 && samples.length >= 3) flags.push('suspect');
  if (r > 50) flags.push('unstable');
  return {
    module: 'srt',
    trials,
    meanMs: round1(m),
    bestMs: round1(best(samples)),
    rangeMs: round1(r),
    sdMs: round1(s),
    score,
    valid: true,
    label: srtLabel(m),
    flags,
  };
}

export function scoreGng(trials: TrialRaw[]): ModuleResult {
  const formal = formalTrials(trials);
  const go = formal.filter((t) => t.kind === 'go');
  const nogo = formal.filter((t) => t.kind === 'nogo');
  const goHits = go.filter((t) => t.correct && !t.foul && t.rtMs != null);
  const samples = goHits.map((t) => t.rtMs as number);
  const hitRate = go.length ? go.filter((t) => t.correct && !t.foul).length / go.length : 0;
  // No-Go foul = clicked; correct false also means fail inhibit
  const fa = nogo.filter((t) => t.foul || t.correct === false).length;
  const faRate = nogo.length ? fa / nogo.length : 0;

  if (samples.length === 0) {
    return {
      module: 'gng',
      trials,
      meanMs: null,
      bestMs: null,
      rangeMs: null,
      sdMs: null,
      score: 0,
      valid: false,
      label: '无效',
      flags: ['invalid'],
      hitRate: round1(hitRate * 100) / 100,
      falseAlarmRate: round1(faRate * 100) / 100,
    };
  }

  const m = mean(samples);
  let base = scoreFromMs(m, GNG_ANCHORS);
  let penalty = 0;
  if (faRate > 0.1) penalty += Math.ceil((faRate - 0.1) / 0.05) * 10;
  if (hitRate < 0.9) penalty += Math.ceil((0.9 - hitRate) / 0.05) * 8;
  const score = round1(clamp(base - penalty, 0, 100));

  return {
    module: 'gng',
    trials,
    meanMs: round1(m),
    bestMs: round1(best(samples)),
    rangeMs: samples.length >= 2 ? round1(range(samples)) : 0,
    sdMs: round1(sd(samples)),
    score,
    valid: true,
    label: srtLabel(m - 40), // rough display
    flags: [],
    hitRate: round1(hitRate * 1000) / 1000,
    falseAlarmRate: round1(faRate * 1000) / 1000,
  };
}

export function scoreCrt(trials: TrialRaw[]): ModuleResult {
  const formal = formalTrials(trials);
  const correct = formal.filter((t) => t.correct && !t.foul && t.rtMs != null);
  const samples = correct.map((t) => t.rtMs as number);
  const accuracy = formal.length ? formal.filter((t) => t.correct).length / formal.length : 0;

  if (samples.length === 0) {
    return {
      module: 'crt',
      trials,
      meanMs: null,
      bestMs: null,
      rangeMs: null,
      sdMs: null,
      score: 0,
      valid: false,
      label: '无效',
      flags: ['invalid'],
      accuracy,
    };
  }

  const m = mean(samples);
  let base = scoreFromMs(m, CRT_ANCHORS);
  if (accuracy < 0.9) base = Math.min(base, 59);
  const score = round1(clamp(base, 0, 100));

  return {
    module: 'crt',
    trials,
    meanMs: round1(m),
    bestMs: round1(best(samples)),
    rangeMs: samples.length >= 2 ? round1(range(samples)) : 0,
    sdMs: round1(sd(samples)),
    score,
    valid: true,
    label: srtLabel(m - 60),
    flags: [],
    accuracy: round1(accuracy * 1000) / 1000,
  };
}

export function scoreAim(trials: TrialRaw[]): ModuleResult {
  const samples = formalTrials(trials)
    .filter((t) => !t.foul && t.rtMs != null)
    .map((t) => t.rtMs as number);

  if (samples.length < 30) {
    return {
      module: 'aim',
      trials,
      meanMs: samples.length ? round1(mean(samples)) : null,
      bestMs: samples.length ? round1(best(samples)) : null,
      rangeMs: samples.length >= 2 ? round1(range(samples)) : null,
      sdMs: samples.length ? round1(sd(samples)) : null,
      score: 0,
      valid: false,
      label: '无效',
      flags: ['invalid'],
    };
  }

  const m = mean(samples);
  const score = round1(clamp(scoreFromMs(m, AIM_ANCHORS), 0, 100));
  return {
    module: 'aim',
    trials,
    meanMs: round1(m),
    bestMs: round1(best(samples)),
    rangeMs: round1(range(samples)),
    sdMs: round1(sd(samples)),
    score,
    valid: true,
    label: aimLabel(m),
    flags: [],
  };
}

export function scoreModule(id: ModuleId, trials: TrialRaw[]): ModuleResult {
  switch (id) {
    case 'srt':
      return scoreSrt(trials);
    case 'gng':
      return scoreGng(trials);
    case 'crt':
      return scoreCrt(trials);
    case 'aim':
      return scoreAim(trials);
  }
}

export function composeSession(
  modules: ModuleResult[],
  platform: PlatformKind,
  createdAt = Date.now(),
): SessionResult {
  const byId = (id: ModuleId) => modules.find((m) => m.module === id);
  const sSrt = byId('srt')?.score ?? 0;
  const sGng = byId('gng')?.score ?? 0;
  const sCrt = byId('crt')?.score ?? 0;
  const sAim = byId('aim')?.score ?? 0;
  const score = round1(0.3 * sSrt + 0.25 * sGng + 0.2 * sCrt + 0.25 * sAim);
  const suspect = modules.some((m) => m.flags.includes('suspect'));
  return {
    modules,
    score,
    rank: rankFromScore(score),
    srtMeanMs: byId('srt')?.meanMs ?? null,
    createdAt,
    platform,
    suspect,
  };
}
